<template>
  <div class="mains axis-page" v-if="detail">
    <div class="campus">
      功能模块 <span>></span>
      <a @click="goDubbing">趣配音</a> <span>></span>
      <a @click="back">上传视频</a> <span>></span>
      配音句子配置
    </div>

    <el-card shadow="never" class="node-card">
      <div slot="header" class="card-title">
        <span>配音句子配置</span>
        <el-tag size="mini" effect="plain" :type="detail.status === 'PUBLISHED' ? 'success' : 'info'">
          {{ detail.statusLabel }}
        </el-tag>
        <div class="ct-right">
          <el-button size="small" :loading="acting === 'draft'" :disabled="locked && acting !== 'draft'"
                     @click="save(false)">保存草稿
          </el-button>
          <el-button size="small" type="primary" :loading="acting === 'publish'"
                     :disabled="locked && acting !== 'publish'" @click="publish">完成并上架
          </el-button>
          <el-button v-if="detail.status === 'PUBLISHED'" size="small" :loading="acting === 'offline'"
                     :disabled="locked && acting !== 'offline'" @click="offline">下架
          </el-button>
          <el-button size="small" :disabled="locked" @click="back">返回上传视频</el-button>
        </div>
        <p class="card-sub">
          每句话要自己标出开始和结束，按时间先后排列，不会自动分句。编辑时，蓝块是这句占的时段，橙线是播到的位置，拖两端的点可以微调起止。</p>
      </div>

      <div class="node-fixed">
        <div class="node-video">
          <div class="player">
            <video
              v-if="detail.videoUrl"
              ref="video"
              class="scr"
              :src="detail.videoUrl"
              @click="togglePlay"
              @play="onPlay"
              @pause="onVideoPause"
              @ended="playing = false"
              @timeupdate="onTime"
              @seeked="onSeeked"
              @loadedmetadata="onMeta"
            />
            <div v-else class="scr missing">原视频地址为空</div>
            <div v-if="detail.videoUrl && !playing" class="mask" @click="togglePlay">
              <el-button class="bigplay" type="primary" circle icon="el-icon-caret-right" @click.stop="togglePlay"/>
              <span>视频播放区 · 暂停后取当前时间点</span>
            </div>
            <div class="ctrl" @click.stop>
              <div class="bar" :class="{ idle: !panelOpen }" ref="bar" @mousedown="seekBar">
                <div class="fill" :style="fillStyle"></div>
                <div class="head" :style="{ left: headLeft }"></div>
                <div v-show="panelOpen" class="tick st" :style="{ left: startLeft }"
                     @mousedown.stop.prevent="startDrag('start', $event)"></div>
                <div v-show="panelOpen" class="tick ed" :style="{ left: endLeft }"
                     @mousedown.stop.prevent="startDrag('end', $event)"></div>
              </div>
              <div class="ctrl-row">
                <span>{{ clock(currentMs) }} / {{ totalClock }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="node-panel">
          <div class="panel-h">{{ panelTitle }}</div>
          <div class="panel-body">
            <div v-if="!panelOpen" class="panel-add">
              <el-button type="primary" icon="el-icon-plus" @click="openAdd">新增配音句</el-button>
            </div>
            <el-form v-else size="small" class="axis-form" @submit.native.prevent>
              <div class="panel-row">
                <el-form-item label="起始点">
                  <el-input
                    v-model="form.startText"
                    class="time-input"
                    placeholder="00:00.0"
                    @focus="activeEdge = 'startText'"
                    @blur="onTimeBlur"
                  />
                </el-form-item>
                <el-form-item label="结束点">
                  <el-input
                    v-model="form.endText"
                    class="time-input"
                    placeholder="00:00.0"
                    @focus="activeEdge = 'endText'"
                    @blur="onTimeBlur"
                  />
                </el-form-item>
                <el-button size="mini" @mousedown.native.prevent @click="takeCurrent">暂停取当前点</el-button>
                <el-button size="mini" @mousedown.native.prevent @click="previewSpan">预览</el-button>
                <el-button size="mini" @mousedown.native.prevent @click="resetTimes">重置</el-button>
                <span v-if="spanText" class="dur" :class="{ short: spanShort }">{{ spanText }}</span>
              </div>
              <el-alert class="formula" type="info" :closable="false">
                <div>句子时长 = 结束点 − 起始点 · 起止两个锚点可在进度条上拖动微调（精度 0.1 秒）</div>
                <div>时段不得与其他句子重叠 · 时长低于 1.5 秒将标黄提醒</div>
              </el-alert>
              <el-input v-model="form.textEn" type="textarea" :rows="2" maxlength="500"
                        placeholder="英文文本（必填，建议不超过 15 个词）"/>
              <el-input v-model="form.textZh" type="textarea" :rows="2" maxlength="500"
                        placeholder="中文文本（选填，用于后台对照）"/>
              <div class="f-hint">示范原声直接取自当前视频的句子时段，点击预览可检查。</div>
              <div class="panel-actions">
                <el-button id="panelSave" size="small" type="primary" :loading="lineSaving" @mousedown.native.prevent
                           @click="addLine">{{ editingKey ? '保存修改' : '保存' }}
                </el-button>
                <el-button size="small" @mousedown.native.prevent @click="closePanel">取消</el-button>
              </div>
            </el-form>
          </div>
        </div>
      </div>

      <div class="node-scroll">
        <el-table
          :data="lines"
          border
          size="mini"
          :header-cell-style="headerStyle"
          :row-class-name="rowClass"
          empty-text="暂无数据，请在右侧面板添加第一句配音"
          @row-click="choose"
        >
          <el-table-column type="index" label="#" width="56" align="center"/>
          <el-table-column label="起始点" width="110" align="center">
            <template slot-scope="scope"><span class="time-link">{{ clock(scope.row.startMs) }}</span></template>
          </el-table-column>
          <el-table-column label="结束点" width="110" align="center">
            <template slot-scope="scope"><span class="time-link">{{ clock(scope.row.endMs) }}</span></template>
          </el-table-column>
          <el-table-column label="句子文本" min-width="240" align="left">
            <template slot-scope="scope">
              <div class="en">{{ scope.row.textEn || '（英文空）' }}</div>
              <div v-if="scope.row.textZh" class="zh">{{ scope.row.textZh }}</div>
            </template>
          </el-table-column>
          <el-table-column label="时长(自动)" width="110" align="center">
            <template slot-scope="scope">
              <span class="dur" :class="{ short: short(scope.row) }">{{ spanOf(scope.row) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120" align="center">
            <template slot-scope="scope">
              <el-button type="text" size="mini" :disabled="!!removingKey" @click.stop="edit(scope.row)">编辑
              </el-button>
              <el-button type="text" size="mini" class="link-danger" :loading="removingKey === scope.row.clientKey"
                         :disabled="!!removingKey && removingKey !== scope.row.clientKey"
                         @click.stop="remove(scope.row)">删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>

    <el-dialog title="还有未保存的修改" :visible.sync="leaveOpen" width="420px" :close-on-click-modal="false"
               :show-close="false">
      <p>可以先保存草稿，放弃后本次修改不会留下，取消则回到打轴页。</p>
      <span slot="footer">
        <el-button size="small" :disabled="leaveSaving" @click="finishLeave(false)">取消</el-button>
        <el-button size="small" :disabled="leaveSaving" @click="abandon">放弃</el-button>
        <el-button size="small" type="primary" :loading="leaveSaving" @click="saveAndLeave">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
/** 打轴页。句子用本地编号跟踪，删除前序句不会串改正在编辑的句子。 */
let seed = 1;

function key() {
  seed += 1;
  return 'line-' + seed;
}

function formatClock(ms) {
  const tenth = Math.round(Number(ms) / 100);
  const mins = Math.floor(tenth / 600);
  const secs = Math.floor((tenth % 600) / 10);
  return String(mins).padStart(2, '0') + ':' + String(secs).padStart(2, '0') + '.' + (tenth % 10);
}

/** 接受秒数或 mm:ss.s，并且必须落在 0.1 秒刻度上。 */
function parseTime(text) {
  const raw = String(text == null ? '' : text).trim();
  const clock = raw.match(/^(\d+):([0-5]?\d)(?:\.(\d))?$/);
  let seconds = null;
  if (clock) seconds = Number(clock[1]) * 60 + Number(clock[2]) + (clock[3] ? Number(clock[3]) / 10 : 0);
  else if (/^\d+(?:\.\d)?$/.test(raw)) seconds = Number(raw);
  if (seconds == null || seconds < 0) return null;
  const tenth = Math.round(seconds * 10);
  if (Math.abs(seconds * 10 - tenth) > 0.001) return null;
  return tenth * 100;
}

function emptyForm() {
  return {startText: '00:00.0', endText: '00:05.0', textEn: '', textZh: ''};
}

export default {
  name: 'dubbingAxis',
  data() {
    return {
      detail: null,
      lines: [],
      form: emptyForm(),
      panelOpen: false,
      editingKey: null,
      selectedKey: null,
      dirty: false,
      leaveOpen: false,
      pendingNext: null,
      playing: false,
      currentMs: 0,
      mediaMs: 0,
      /** 播放中点了某一句后，播到这句结束就暂停。0 表示不拦截。 */
      stopAtMs: 0,
      stopFromMs: 0,
      /** 跳转完成前忽略进度，避免用点击前的旧时间误停。 */
      awaitingSeek: false,
      stopTimer: null,
      frameId: 0,
      activeEdge: 'endText',
      committed: {start: '00:00.0', end: '00:05.0'},
      skipSaveOnce: false,
      seeked: false,
      /** 顶栏请求：draft 保存草稿，publish 上架，offline 下架。 */
      acting: '',
      /** 离开确认框里的保存。 */
      leaveSaving: false,
      /** 句子「保存」连点锁。校验失败会立刻放开。 */
      lineSaving: false,
      /** 正在确认删除的句子。 */
      removingKey: '',
    };
  },
  computed: {
    /** 顶栏或离开框有请求时，其它提交按钮先停住。 */
    locked() {
      return !!(this.acting || this.leaveSaving);
    },
    panelTitle() {
      if (!this.panelOpen) return '配音句';
      return this.editingKey ? '编辑配音句' : '添加配音句';
    },
    selected() {
      return this.lines.find((line) => line.clientKey === this.selectedKey) || null;
    },
    totalClock() {
      const ms = this.durationLimit();
      return ms ? formatClock(ms) : '00:00.0';
    },
    spanText() {
      const start = parseTime(this.form.startText);
      const end = parseTime(this.form.endText);
      if (start == null || end == null || end < start) return '';
      return ((end - start) / 1000).toFixed(1) + 's';
    },
    spanShort() {
      const start = parseTime(this.form.startText);
      const end = parseTime(this.form.endText);
      return start != null && end != null && end - start < 1500;
    },
    fillStyle() {
      if (!this.panelOpen) return {display: 'none'};
      const start = parseTime(this.form.startText);
      const end = parseTime(this.form.endText);
      const dur = this.durationLimit();
      if (start == null || end == null || dur <= 0) return {display: 'none'};
      const left = Math.min(dur, Math.max(0, Math.min(start, end)));
      const right = Math.min(dur, Math.max(0, Math.max(start, end)));
      return {left: (left / dur * 100) + '%', width: ((right - left) / dur * 100) + '%'};
    },
    startLeft() {
      return this.edgeLeft(parseTime(this.form.startText));
    },
    endLeft() {
      return this.edgeLeft(parseTime(this.form.endText));
    },
    headLeft() {
      return this.edgeLeft(this.currentMs);
    },
  },
  created() {
    this.load();
  },
  beforeRouteLeave(to, from, next) {
    if (!this.dirty && !this.unsavedForm()) return next();
    this.pendingNext = next;
    this.leaveOpen = true;
  },
  beforeDestroy() {
    this.unbindDrag();
    this.clearSentenceStop();
  },
  methods: {
    auth() {
      return {Authorization: 'Bearer ' + window.sessionStorage.getItem('userinfo').replace(/"/g, '')};
    },
    headerStyle() {
      return {background: '#F2F2F2', color: '#333', fontWeight: '600'};
    },
    clock(ms) {
      return formatClock(ms);
    },
    short(row) {
      return row.endMs - row.startMs < 1500;
    },
    spanOf(row) {
      return ((row.endMs - row.startMs) / 1000).toFixed(1) + 's';
    },
    durationLimit() {
      if (this.detail && this.detail.durationMs > 0) return this.detail.durationMs;
      return this.mediaMs || 0;
    },
    edgeLeft(ms) {
      const dur = this.durationLimit();
      if (ms == null || dur <= 0) return '0%';
      return (Math.min(dur, Math.max(0, ms)) / dur * 100) + '%';
    },
    defaultEndText() {
      const limit = this.durationLimit();
      if (limit > 500) return formatClock(Math.max(500, Math.min(5000, Math.floor(limit / 100) * 100)));
      return '00:05.0';
    },
    load() {
      return this.$http({
        url: '/edu/dubbing/materials/' + this.$route.query.materialId,
        method: 'GET',
        headers: this.auth()
      }).then((res) => {
        if (res.data.code !== 0) return this.$message.error(res.data.message);
        this.detail = res.data.data;
        this.lines = (res.data.data.lines || []).map((line) => ({
          ...line,
          clientKey: line.clientKey || key(),
        }));
        this.lines.sort((a, b) => a.startMs - b.startMs);
        this.selectedKey = this.lines.length ? this.lines[0].clientKey : null;
        this.dirty = false;
        this.seeked = false;
        this.closePanel();
        this.$nextTick(() => {
          if (this.selected) this.seekTo(this.selected.startMs);
        });
      });
    },
    onPlay() {
      this.playing = true;
      const video = this.$refs.video;
      if (!video || !this.stopAtMs) return;
      const now = video.currentTime * 1000;
      if (this.awaitingSeek && now >= this.stopFromMs - 200) this.awaitingSeek = false;
      if (!this.awaitingSeek) this.watchSentenceEnd();
    },
    onVideoPause() {
      this.playing = false;
      this.clearStopWatch();
    },
    onTime() {
      const video = this.$refs.video;
      if (!video) return;
      this.currentMs = Math.round(video.currentTime * 1000);
      this.pauseAtSentenceEnd(video);
    },
    /** 跳转落定后再开始盯句尾。timeupdate 大约 250ms 一次，短句会直接跳过结束点。 */
    onSeeked() {
      if (!this.stopAtMs || !this.awaitingSeek) return;
      const video = this.$refs.video;
      if (!video) return;
      const now = video.currentTime * 1000;
      if (now < this.stopFromMs - 200) return;
      this.awaitingSeek = false;
      if (now >= this.stopAtMs - 40) {
        this.finishSentence(video);
        return;
      }
      this.watchSentenceEnd();
    },
    pauseAtSentenceEnd(video) {
      if (!this.stopAtMs || this.awaitingSeek || video.paused) return;
      const now = video.currentTime * 1000;
      if (now < this.stopFromMs - 200) return;
      if (now >= this.stopAtMs - 40) this.finishSentence(video);
    },
    /** 点击表格行：从这句开头播放，到结束点暂停。 */
    previewLine(line) {
      const video = this.$refs.video;
      if (!video || !line) return;
      this.clearSentenceStop();
      this.stopAtMs = line.endMs;
      this.stopFromMs = line.startMs;
      this.awaitingSeek = true;
      this.seekTo(line.startMs);
      const pending = video.play();
      if (pending && pending.catch) pending.catch(() => {
      });
      const wait = Math.max(line.endMs - line.startMs, 80);
      this.stopTimer = setTimeout(() => this.onSentenceClock(), wait);
    },
    /** 编辑时如果本来就在播放，同样播到这句结束再停。 */
    armSentenceEnd(line) {
      const video = this.$refs.video;
      this.clearSentenceStop();
      if (!video || video.paused || !line) return;
      this.stopAtMs = line.endMs;
      this.stopFromMs = line.startMs;
      this.awaitingSeek = true;
      const wait = Math.max(line.endMs - line.startMs, 80);
      this.stopTimer = setTimeout(() => this.onSentenceClock(), wait);
    },
    /** 到点后若进度事件漏掉句尾，用定时器再停一次。跳转未完成就稍后重试。 */
    onSentenceClock(tries) {
      this.stopTimer = null;
      const video = this.$refs.video;
      if (!video || !this.stopAtMs) return;
      const now = video.currentTime * 1000;
      const left = tries == null ? 12 : tries;
      if (now < this.stopFromMs - 200) {
        if (left <= 0) {
          this.awaitingSeek = false;
          return;
        }
        this.stopTimer = setTimeout(() => this.onSentenceClock(left - 1), 80);
        return;
      }
      this.awaitingSeek = false;
      if (video.paused) return;
      if (now >= this.stopAtMs - 80) this.finishSentence(video);
      else this.watchSentenceEnd();
    },
    watchSentenceEnd() {
      const video = this.$refs.video;
      if (!video || !this.stopAtMs || video.paused) return;
      const tick = () => {
        this.clearStopWatch();
        const current = this.$refs.video;
        if (!current || !this.stopAtMs || this.awaitingSeek || current.paused) return;
        if (current.currentTime * 1000 >= this.stopAtMs - 40) {
          this.finishSentence(current);
          return;
        }
        this.armFrame(tick);
        const wait = this.stopAtMs - current.currentTime * 1000;
        this.stopTimer = setTimeout(tick, Math.max(wait, 40));
      };
      tick();
    },
    armFrame(tick) {
      const video = this.$refs.video;
      if (!video || !video.requestVideoFrameCallback) return;
      this.frameId = video.requestVideoFrameCallback(() => {
        this.frameId = 0;
        tick();
      });
    },
    finishSentence(video) {
      const end = this.stopAtMs;
      if (!end) return;
      this.stopAtMs = 0;
      this.awaitingSeek = false;
      this.clearStopWatch();
      video.pause();
      video.currentTime = end / 1000;
    },
    clearStopWatch() {
      if (this.stopTimer) {
        clearTimeout(this.stopTimer);
        this.stopTimer = null;
      }
      const video = this.$refs.video;
      if (video && this.frameId && video.cancelVideoFrameCallback) {
        video.cancelVideoFrameCallback(this.frameId);
      }
      this.frameId = 0;
    },
    clearSentenceStop() {
      this.stopAtMs = 0;
      this.stopFromMs = 0;
      this.awaitingSeek = false;
      this.clearStopWatch();
    },
    onMeta() {
      const video = this.$refs.video;
      if (video && isFinite(video.duration)) this.mediaMs = Math.round(video.duration * 1000);
      if (!this.seeked && this.selected) {
        this.seeked = true;
        this.seekTo(this.selected.startMs);
      }
    },
    togglePlay() {
      const video = this.$refs.video;
      if (!video) return;
      if (video.paused) {
        const pending = video.play();
        if (pending && pending.catch) pending.catch(() => {
        });
      } else {
        video.pause();
      }
    },
    seekTo(ms) {
      const video = this.$refs.video;
      if (!video || ms == null) return;
      const apply = () => {
        video.currentTime = ms / 1000;
      };
      if (video.readyState >= 1) apply();
      else video.addEventListener('loadedmetadata', apply, {once: true});
    },
    seekBar(event) {
      if (event.target.classList && event.target.classList.contains('tick')) return;
      const video = this.$refs.video;
      const bar = this.$refs.bar;
      const dur = this.durationLimit() / 1000;
      if (!video || !bar || dur <= 0) return;
      const rect = bar.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      this.clearSentenceStop();
      video.currentTime = ratio * dur;
    },
    /** 拖动进度条两端锚点，松手后按 0.1 秒校验。 */
    startDrag(which, event) {
      event.preventDefault();
      const bar = this.$refs.bar;
      const before = which === 'start' ? this.form.startText : this.form.endText;
      const move = (e) => {
        if (!bar) return;
        const durMs = this.durationLimit();
        const dur = durMs > 0 ? durMs / 1000 : (this.$refs.video && isFinite(this.$refs.video.duration) ? this.$refs.video.duration : 0);
        if (dur <= 0) return;
        const rect = bar.getBoundingClientRect();
        let t = Math.round(Math.min(dur, Math.max(0, (e.clientX - rect.left) / rect.width * dur)) * 10) / 10;
        const start = parseTime(this.form.startText);
        const end = parseTime(this.form.endText);
        const startSec = start == null ? 0 : start / 1000;
        const endSec = end == null ? dur : end / 1000;
        if (which === 'start') {
          if (t > endSec - 0.5) t = Math.max(0, Math.round((endSec - 0.5) * 10) / 10);
          this.form.startText = formatClock(t * 1000);
        } else {
          if (t < startSec + 0.5) t = Math.min(dur, Math.round((startSec + 0.5) * 10) / 10);
          this.form.endText = formatClock(t * 1000);
        }
      };
      const up = () => {
        this.unbindDrag();
        if (!this.commitTimes()) return;
        const after = which === 'start' ? this.form.startText : this.form.endText;
        if (after === before) return;
        this.$message.success((which === 'start' ? '起始' : '结束') + '点已调整为 ' + after);
        const ms = parseTime(after);
        if (ms != null) this.seekTo(ms);
      };
      this.dragMove = move;
      this.dragUp = up;
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    },
    unbindDrag() {
      if (this.dragMove) document.removeEventListener('mousemove', this.dragMove);
      if (this.dragUp) document.removeEventListener('mouseup', this.dragUp);
      this.dragMove = null;
      this.dragUp = null;
    },
    rememberTimes() {
      this.committed = {start: this.form.startText, end: this.form.endText};
    },
    onTimeBlur(event) {
      const label = event && event.relatedTarget ? (event.relatedTarget.innerText || event.relatedTarget.textContent || '') : '';
      if (/取消|重置|取当前|预览/.test(label)) return;
      const ok = this.commitTimes();
      if (!ok && /保存/.test(label)) this.skipSaveOnce = true;
    },
    /** 修改结束（失焦或松手）后验收。未通过则回退。 */
    commitTimes() {
      const same = this.form.startText === this.committed.start && this.form.endText === this.committed.end;
      const result = this.checkTimes();
      if (same) return result.ok;
      if (!result.ok) {
        this.form.startText = this.committed.start;
        this.form.endText = this.committed.end;
        this.$message.error(result.msg || '起止时间未通过校验，已恢复修改前的时间');
        return false;
      }
      this.rememberTimes();
      return true;
    },
    checkTimes() {
      const rawStart = String(this.form.startText || '').trim();
      const rawEnd = String(this.form.endText || '').trim();
      if (!rawStart || !rawEnd) return {ok: false, msg: '请填写起止时间'};
      const start = parseTime(rawStart);
      const end = parseTime(rawEnd);
      if (start == null) return {ok: false, msg: '起始时间格式为 mm:ss.s，或直接填秒数'};
      if (end == null) return {ok: false, msg: '结束时间格式为 mm:ss.s，或直接填秒数'};
      const limit = this.durationLimit();
      if (start < 0) return {ok: false, msg: '起始时间不能小于 00:00.0'};
      if (limit > 0 && start > limit) return {ok: false, msg: '起始时间不能大于视频时长 ' + formatClock(limit)};
      if (end < 0) return {ok: false, msg: '结束时间不能小于 00:00.0'};
      if (limit > 0 && end > limit) return {ok: false, msg: '结束时间不能大于视频时长 ' + formatClock(limit)};
      if (start > end) return {ok: false, msg: '开始时间不能晚于结束时间'};
      if (end - start < 500) return {ok: false, msg: '结束点须晚于起始点至少 0.5 秒'};
      const hit = this.lines.find((line) => line.clientKey !== this.editingKey && start < line.endMs && end > line.startMs);
      if (hit) {
        const name = hit.textEn || formatClock(hit.startMs);
        return {
          ok: false,
          msg: '与已有句子时段重叠：「' + name + '」（' + formatClock(hit.startMs) + ' - ' + formatClock(hit.endMs) + '）'
        };
      }
      return {ok: true, msg: '', start, end};
    },
    /** 从起始点播到结束点，到点自动暂停，用来核对这两个时间是否对准。 */
    previewSpan() {
      const video = this.$refs.video;
      if (!video) return this.$message.warning('当前没有可播放的视频');
      const start = parseTime(String(this.form.startText || '').trim());
      const end = parseTime(String(this.form.endText || '').trim());
      if (start == null) return this.$message.error('起始时间格式为 mm:ss.s，或直接填秒数');
      if (end == null) return this.$message.error('结束时间格式为 mm:ss.s，或直接填秒数');
      if (end <= start) return this.$message.error('结束点要晚于起始点');
      const limit = this.durationLimit();
      if (limit > 0 && end > limit) return this.$message.error('结束时间不能大于视频时长 ' + formatClock(limit));
      this.previewLine({startMs: start, endMs: end});
    },
    takeCurrent() {
      const video = this.$refs.video;
      if (!video) return this.$message.warning('当前没有可暂停的视频');
      video.pause();
      const tenth = Math.round(video.currentTime * 10);
      const field = this.activeEdge === 'startText' ? 'startText' : 'endText';
      this.form[field] = formatClock(tenth * 100);
      this.commitTimes();
    },
    resetTimes() {
      const line = this.editingKey ? this.lines.find((item) => item.clientKey === this.editingKey) : null;
      if (line) {
        this.form.startText = formatClock(line.startMs);
        this.form.endText = formatClock(line.endMs);
      } else {
        const range = this.nextRange();
        this.form.startText = range.start;
        this.form.endText = range.end;
      }
      this.rememberTimes();
    },
    /** 新句子默认接在最后一句后面，避免一打开就和已有时段重叠。 */
    nextRange() {
      const last = this.lines.length ? this.lines[this.lines.length - 1] : null;
      const start = last ? last.endMs : 0;
      const limit = this.durationLimit();
      let end = start + 5000;
      if (limit > 0) end = Math.min(end, Math.floor(limit / 100) * 100);
      if (end - start < 500) return {start: '00:00.0', end: this.defaultEndText()};
      return {start: formatClock(start), end: formatClock(end)};
    },
    openAdd() {
      this.lineSaving = false;
      const range = this.nextRange();
      this.editingKey = null;
      this.form = emptyForm();
      this.form.startText = range.start;
      this.form.endText = range.end;
      this.activeEdge = 'endText';
      this.panelOpen = true;
      this.rememberTimes();
    },
    closePanel() {
      this.lineSaving = false;
      this.panelOpen = false;
      this.editingKey = null;
      this.form = emptyForm();
    },
    /** 把右侧表单写入句子列表。失败返回提示，已弹过消息时返回 skip。 */
    tryCommitLine() {
      if (this.skipSaveOnce) {
        this.skipSaveOnce = false;
        return 'skip';
      }
      if (!this.commitTimes()) {
        const checked = this.checkTimes();
        if (!checked.ok) return checked.msg || '请填写起止时间';
        return 'skip';
      }
      const checked = this.checkTimes();
      if (!checked.ok) return checked.msg || '请填写起止时间';
      const textEn = String(this.form.textEn || '').trim();
      if (!textEn) return '请填写句子文本';
      const old = this.editingKey ? this.lines.find((line) => line.clientKey === this.editingKey) : null;
      const row = {
        clientKey: this.editingKey || key(),
        startMs: checked.start,
        endMs: checked.end,
        textEn,
        textZh: String(this.form.textZh || '').trim(),
        role: old ? (old.role || old.roleName || '') : '',
        needDub: true,
      };
      const index = this.lines.findIndex((line) => line.clientKey === row.clientKey);
      if (index < 0) this.lines.push(row);
      else this.$set(this.lines, index, {...old, ...row});
      this.lines.sort((a, b) => a.startMs - b.startMs);
      this.selectedKey = row.clientKey;
      this.dirty = true;
      this.panelOpen = false;
      this.editingKey = null;
      const words = textEn.split(/\s+/).filter(Boolean);
      if (words.length > 15) this.$message.warning('英文超过 15 个词，建议拆句');
      this.$message.success(old ? '句子已更新' : '句子已添加，时长 ' + ((checked.end - checked.start) / 1000).toFixed(1) + 's');
      return '';
    },
    addLine() {
      if (this.lineSaving) return;
      this.lineSaving = true;
      const error = this.tryCommitLine();
      if (error && error !== 'skip') this.$message.error(error);
      // 没写进去就马上恢复；写进去后面板会收起。
      if (error) this.lineSaving = false;
    },
    edit(line) {
      if (this.removingKey) return;
      this.lineSaving = false;
      this.editingKey = line.clientKey;
      this.selectedKey = line.clientKey;
      this.form = {
        startText: formatClock(line.startMs),
        endText: formatClock(line.endMs),
        textEn: line.textEn || '',
        textZh: line.textZh || '',
      };
      this.activeEdge = 'endText';
      this.panelOpen = true;
      this.rememberTimes();
      this.armSentenceEnd(line);
      this.seekTo(line.startMs);
    },
    choose(line) {
      this.selectedKey = line.clientKey;
      this.previewLine(line);
    },
    remove(line) {
      if (this.removingKey) return;
      this.removingKey = line.clientKey;
      const name = line.textEn || formatClock(line.startMs);
      this.$confirm('确定删除句子「' + name + '」吗？', '删除确认').then(() => {
        if (this.editingKey === line.clientKey) this.closePanel();
        this.lines = this.lines.filter((item) => item.clientKey !== line.clientKey);
        if (this.selectedKey === line.clientKey) this.selectedKey = this.lines.length ? this.lines[0].clientKey : null;
        this.dirty = true;
        this.$message.success('已删除');
      }).catch(() => {
      }).then(() => {
        this.removingKey = '';
      });
    },
    unsavedForm() {
      if (!this.panelOpen) return false;
      if (!this.editingKey) {
        return !!(String(this.form.textEn || '').trim() || String(this.form.textZh || '').trim()
          || this.form.startText !== this.committed.start || this.form.endText !== this.committed.end);
      }
      const line = this.lines.find((item) => item.clientKey === this.editingKey);
      if (!line) return true;
      return this.form.startText !== formatClock(line.startMs)
        || this.form.endText !== formatClock(line.endMs)
        || (this.form.textEn || '') !== (line.textEn || '')
        || (this.form.textZh || '') !== (line.textZh || '');
    },
    payload() {
      return {
        revision: this.detail.revision,
        title: this.detail.title,
        lines: this.lines.map((line) => ({
          startMs: line.startMs,
          endMs: line.endMs,
          textEn: line.textEn,
          textZh: line.textZh,
          role: line.role || line.roleName,
          needDub: true,
        })),
      };
    },
    // 保存失败时保留编辑页。saved=false 不能当成成功。
    // lock=leave 时只转离开框的保存按钮，避免和顶栏草稿抢同一把锁。
    save(thenPublish, lock) {
      if (this.acting || this.leaveSaving) return Promise.resolve(false);
      if (lock === 'leave') this.leaveSaving = true;
      else this.acting = thenPublish ? 'publish' : 'draft';
      const clear = () => {
        if (lock === 'leave') this.leaveSaving = false;
        else this.acting = '';
      };
      return this.$http({
        url: '/edu/dubbing/materials/' + this.detail.materialId + '/lines',
        method: 'PUT',
        headers: this.auth(),
        data: this.payload()
      })
        .then((res) => {
          if (res.data.code !== 0) {
            this.$message.error(res.data.message);
            return false;
          }
          const issues = res.data.data.issues || [];
          if (!res.data.data.saved) {
            return this.showIssues(issues).then(() => {
              this.$message.error('草稿没有保存');
              return false;
            });
          }
          this.detail.revision = res.data.data.revision;
          this.dirty = false;
          if (thenPublish) return this.publishNow();
          return this.showIssues(issues).then(() => {
            this.$message.success('草稿已保存');
            return true;
          });
        }).catch(() => false)
        .then((ok) => {
          clear();
          return ok;
        }, () => {
          clear();
          return false;
        });
    },
    publish() {
      if (this.acting || this.leaveSaving) return;
      if (!this.lines.length) return this.$message.error('请先添加至少一句配音');
      const missing = this.lines.find((line) => !String(line.textEn || '').trim());
      if (missing) return this.$message.error('句子英文不能为空');
      this.save(true);
    },
    publishNow() {
      return this.$http({
        url: '/edu/dubbing/materials/' + this.detail.materialId + '/publish',
        method: 'POST',
        headers: this.auth(),
        data: {revision: this.detail.revision},
      }).then((res) => {
        if (res.data.code !== 0) {
          this.$message.error(res.data.message);
          return false;
        }
        if (res.data.data.revision) this.detail.revision = res.data.data.revision;
        const published = !!res.data.data.published;
        const shown = this.showIssues(res.data.data.issues || []).then(() => {
          if (published) this.$message.success(res.data.data.message || '资源已上架');
        });
        if (!published) return shown.then(() => false);
        return Promise.all([shown, this.load()]).then(() => true);
      });
    },
    offline() {
      if (this.acting || this.leaveSaving) return;
      this.acting = 'offline';
      const clear = () => {
        this.acting = '';
      };
      this.$confirm('下架后不能再配。历史作品仍可播放。', '下架').then(() => {
        return this.$http({
          url: '/edu/dubbing/materials/' + this.detail.materialId + '/offline',
          method: 'POST',
          headers: this.auth()
        })
          .then((res) => {
            if (res.data.code !== 0) return this.$message.error(res.data.message);
            return this.load();
          });
      }).catch(() => {
      }).then(clear, clear);
    },
    /**
     * 校验提醒走 Element Message。
     * 同一轮同步创建时，上一条还是 display:none，offsetHeight 为 0，会叠在同一位置。
     * 等上一条真正显示后再弹下一条，组件才能按高度往下排。
     */
    showIssues(list) {
      const issues = list || [];
      if (!issues.length) return Promise.resolve();
      if (this.$message.closeAll) this.$message.closeAll();
      return new Promise((resolve) => {
        const open = (index) => {
          if (index >= issues.length) {
            resolve();
            return;
          }
          const issue = issues[index];
          this.$message({
            message: issue.message,
            type: issue.level === 'ERROR' ? 'error' : 'warning',
            showClose: true,
            duration: 5000,
          });
          this.$nextTick(() => open(index + 1));
        };
        this.$nextTick(() => open(0));
      });
    },
    goDubbing() {
      if (this.locked) return;
      this.$router.push({name: 'dubbing'});
    },
    back() {
      if (this.locked) return;
      this.$router.push({
        name: 'dubbingVideos',
        query: {
          bookId: this.$route.query.bookId,
          chapterId: this.$route.query.chapterId,
          title: this.$route.query.title,
          seqnum: this.$route.query.seqnum,
        },
      });
    },
    abandon() {
      this.dirty = false;
      this.panelOpen = false;
      this.finishLeave(true);
    },
    saveAndLeave() {
      if (this.leaveSaving || this.acting) return;
      if (this.panelOpen && this.unsavedForm()) {
        const error = this.tryCommitLine();
        if (error) {
          if (error !== 'skip') this.$message.error(error);
          return;
        }
      }
      this.save(false, 'leave').then((ok) => this.finishLeave(ok));
    },
    finishLeave(ok) {
      const next = this.pendingNext;
      this.pendingNext = null;
      this.leaveOpen = false;
      if (next) next(!!ok);
    },
    rowClass({row}) {
      return row.clientKey === this.selectedKey ? 'picked-line' : '';
    },
  },
};
</script>

<style scoped>
.axis-page {
  height: calc(100vh - 150px);
  min-height: 560px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.axis-page /deep/ .node-card.el-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #ededed;
  border-radius: 2px;
}

.axis-page /deep/ .node-card > .el-card__header {
  padding: 0;
  border-bottom: none;
  flex: none;
}

.axis-page /deep/ .node-card > .el-card__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 12px 15px 8px;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 15px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  color: #333;
  font-weight: 600;
  flex-wrap: wrap;
}

.card-title::before {
  content: '';
  width: 2px;
  height: 13px;
  background: #1e9fff;
}

.card-sub {
  flex: 1 0 100%;
  margin: 0;
  padding-left: 10px;
  font-size: 12px;
  color: #999;
  font-weight: 400;
  line-height: 1.6;
}

.ct-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 400;
}

.ct-right /deep/ .el-button {
  margin-left: 0;
}

.node-fixed {
  flex: none;
  display: flex;
  gap: 14px;
  align-items: stretch;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.node-video {
  flex: 0 0 auto;
  width: min(calc(46vh * 16 / 9), 720px);
  max-width: 58%;
  min-width: 420px;
}

.player {
  position: relative;
  width: 100%;
  background: #0f172a;
  border-radius: 3px;
  overflow: hidden;
}

.scr {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #0f172a;
  object-fit: contain;
}

.missing {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 13px;
}

.mask {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #cbd5e1;
  font-size: 12px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.18), rgba(15, 23, 42, 0.38));
  cursor: pointer;
}

.bigplay {
  width: 50px;
  height: 50px;
  min-width: 50px;
  padding: 0;
  font-size: 22px;
  background: rgba(30, 159, 255, 0.92);
  border-color: transparent;
}

.bigplay /deep/ i {
  font-size: 22px;
}

.ctrl {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  padding: 22px 12px 8px;
  color: #cbd5e1;
  font-size: 11px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.75));
}

.bar {
  position: relative;
  height: 4px;
  margin-bottom: 6px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.25);
  cursor: pointer;
}

.fill {
  position: absolute;
  top: 0;
  bottom: 0;
  background: rgba(30, 159, 255, 0.55);
}

.head {
  position: absolute;
  top: -3px;
  width: 2px;
  height: 10px;
  background: #e6a23c;
  transform: translateX(-50%);
}

.tick {
  position: absolute;
  top: -6px;
  z-index: 2;
  width: 10px;
  height: 16px;
  border-radius: 2px;
  cursor: ew-resize;
  transform: translateX(-50%);
}

.tick.st {
  background: #1e9fff;
  border: 2px solid #fff;
}

.tick.ed {
  background: #fff;
  border: 2px solid #1e9fff;
}

.bar.idle .fill,
.bar.idle .tick {
  display: none;
}

.ctrl-row {
  display: flex;
  gap: 8px;
}

.node-panel {
  flex: 1;
  min-width: 360px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e6e6e6;
  border-radius: 2px;
  background: #fafbfc;
}

.panel-h {
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
  font-size: 13px;
  font-weight: 600;
}

.panel-h::before {
  content: '';
  width: 2px;
  height: 13px;
  background: #1e9fff;
}

.panel-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
}

.panel-add {
  flex: 1;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.panel-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.panel-row /deep/ .el-button {
  margin-left: 0;
}

.axis-form /deep/ .el-form-item {
  margin-bottom: 0;
  display: flex;
  align-items: center;
}

.axis-form /deep/ .el-form-item__label {
  float: none;
  color: #999;
  font-size: 12px;
  line-height: 32px;
  padding-right: 6px;
}

.axis-form /deep/ .el-form-item__content {
  line-height: 32px;
}

.axis-form /deep/ .el-textarea {
  margin-bottom: 8px;
}

.time-input {
  width: 96px;
}

.time-input /deep/ .el-input__inner {
  padding: 0 8px;
  font-family: Consolas, Menlo, monospace;
}

.formula.el-alert {
  margin-bottom: 8px;
  padding: 6px 8px;
  background: #ecf5ff;
  align-items: flex-start;
}

.formula /deep/ .el-alert__icon {
  display: none;
}

.formula /deep/ .el-alert__content {
  padding: 0;
}

.formula /deep/ .el-alert__description {
  margin: 0;
  color: #0b7bc4;
  font-size: 12px;
  line-height: 1.8;
  font-family: Consolas, Menlo, monospace;
}

.field-label {
  color: #999;
  font-size: 12px;
}

.f-hint {
  color: #bbb;
  font-size: 12px;
  line-height: 1.5;
}

.audio-field {
  margin-bottom: 8px;
}

.audio-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}

.panel-actions {
  display: flex;
  gap: 8px;
}

.panel-actions /deep/ .el-button {
  margin-left: 0;
}

.node-scroll {
  flex: 1;
  min-height: 120px;
  overflow: auto;
  padding-top: 8px;
}

.node-scroll /deep/ .el-table th,
.node-scroll /deep/ .el-table td {
  border-color: #e6e6e6;
}

.node-scroll /deep/ .el-table td {
  padding: 4px 0;
}

.node-scroll /deep/ .el-table .cell {
  line-height: 1.35;
  font-size: 12px;
}

.node-scroll /deep/ .picked-line td {
  background: #ecf5ff !important;
}

.time-link {
  color: #1e9fff;
  font-family: Consolas, Menlo, monospace;
  cursor: pointer;
}

.en {
  color: #333;
}

.zh {
  color: #999;
  font-size: 11px;
  margin-top: 1px;
}

.dur {
  font-family: Consolas, Menlo, monospace;
  font-weight: 700;
  color: #67c23a;
}

.dur.short {
  color: #e6a23c;
}

.audio-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.audio-text {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.audio-name {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #999;
  font-size: 12px;
  text-align: left;
}

.audio-dur {
  flex: none;
  font-family: Consolas, Menlo, monospace;
  font-weight: 700;
  color: #333;
  font-size: 12px;
}

.audio-name.none {
  color: #ccc;
}

.audio-btn {
  width: 28px;
  height: 28px;
  min-width: 28px;
  padding: 0;
  color: #1e9fff;
  border-color: #1e9fff;
  background: #fff;
}

.audio-btn.on {
  background: #1e9fff;
  color: #fff;
}

.link-danger {
  color: #f56c6c;
}

a {
  cursor: pointer;
  color: #666;
}
</style>
